import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  listSafeguardingReviewsSuccess,
  listSafeguardingReviewsFailure,
  listSafeguardingCasesSuccess,
  listSafeguardingCasesFailure,
  listSafeguardingHistorySuccess,
  listSafeguardingHistoryFailure,
  getSafeguardingReviewSuccess,
  getSafeguardingReviewFailure,
  createSafeguardingReviewSuccess,
  createSafeguardingReviewFailure,
  patchSafeguardingReviewSuccess,
  patchSafeguardingReviewFailure,
  addSafeguardingNotificationSuccess,
  addSafeguardingNotificationFailure,
  linkSafeguardingRecordSuccess,
  linkSafeguardingRecordFailure,
  resolveSafeguardingReviewSuccess,
  resolveSafeguardingReviewFailure,
  listLinkableIncidentsSuccess,
  listLinkableIncidentsFailure,
  linkSafeguardingIncidentSuccess,
  linkSafeguardingIncidentFailure,
  unlinkSafeguardingIncidentSuccess,
  unlinkSafeguardingIncidentFailure,
  getSafeguardingCaseSuccess,
  getSafeguardingCaseFailure,
} from "./actions";
import {
  LIST_SAFEGUARDING_REVIEWS,
  LIST_SAFEGUARDING_CASES,
  LIST_SAFEGUARDING_HISTORY,
  GET_SAFEGUARDING_REVIEW,
  CREATE_SAFEGUARDING_REVIEW,
  PATCH_SAFEGUARDING_REVIEW,
  ADD_SAFEGUARDING_NOTIFICATION,
  LINK_SAFEGUARDING_RECORD,
  RESOLVE_SAFEGUARDING_REVIEW,
  LIST_LINKABLE_INCIDENTS,
  LINK_SAFEGUARDING_INCIDENT,
  UNLINK_SAFEGUARDING_INCIDENT,
  GET_SAFEGUARDING_CASE,
} from "./constants";

// utils/api's response interceptor already returns `response.data`, so a saga's
// `const { data } = …` is the api envelope's own `data`. Some payloads in this
// codebase arrive one level deeper than that; unwrap tolerantly rather than
// reading a fixed depth and silently rendering nothing.
const unwrap = (data, key) => {
  if (!data || typeof data !== "object") return undefined;
  if (data[key] !== undefined) return data[key];
  if (data.data && typeof data.data === "object") return data.data[key];
  return undefined;
};
const unwrapList = (data, key) => {
  const value = unwrap(data, key);
  return Array.isArray(value) ? value : [];
};

// The api's own message — the incident-link guards are meaningful ("that
// incident belongs to another case", "you cannot unlink the originating
// incident"), so never swallow them behind a generic string.
const apiMessage = (e, fallback) => {
  if (typeof e === "string" && e.trim()) return e;
  if (typeof e?.error === "string" && e.error.trim()) return e.error;
  if (typeof e?.message === "string" && e.message.trim() && !e.response)
    return e.message;
  const body = e?.response?.data;
  if (typeof body?.message === "string" && body.message.trim())
    return body.message;
  return fallback;
};

// List the resident's cases (alert-born + manual, open + closed) for the workspace.
export function* listSafeguardingReviewsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId } = payload;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-review`,
    );
    const reviews = data?.reviews || [];
    yield put(listSafeguardingReviewsSuccess(reviews));
    typeof onSuccess === "function" && onSuccess(reviews);
  } catch (e) {
    yield put(listSafeguardingReviewsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching safeguarding cases!"));
  }
}

// List open case references in the location (the "link to existing case" picker).
export function* listSafeguardingCasesSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId } = payload;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-cases`,
    );
    const cases = data?.cases || [];
    yield put(listSafeguardingCasesSuccess(cases));
    typeof onSuccess === "function" && onSuccess(cases);
  } catch (e) {
    yield put(listSafeguardingCasesFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching safeguarding cases!"));
  }
}

// This resident's prior safeguarding incidents + reviews (enquiry link picker).
export function* listSafeguardingHistorySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, excludeReviewUuid, excludeGovernanceUuid } = payload;
  try {
    const params = new URLSearchParams();
    if (excludeReviewUuid) params.set("exclude_review_uuid", excludeReviewUuid);
    if (excludeGovernanceUuid)
      params.set("exclude_governance_uuid", excludeGovernanceUuid);
    const query = params.toString() ? `?${params.toString()}` : "";
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-history${query}`,
    );
    const history = data?.history || [];
    yield put(listSafeguardingHistorySuccess(history));
    typeof onSuccess === "function" && onSuccess(history);
  } catch (e) {
    yield put(listSafeguardingHistoryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching safeguarding history!"));
  }
}

// Hydrate the drawer from a persisted case (review + notifications + linked plans).
export function* getSafeguardingReviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid } = payload;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-review/${uuid}`,
    );
    yield put(getSafeguardingReviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getSafeguardingReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching safeguarding review!"));
  }
}

// Start the case from an alert (triage). The backend stamps the source alert
// to monitoring and back-points it at the new review.
export function* createSafeguardingReviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/safeguarding-review`,
      formData,
    );
    yield put(createSafeguardingReviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Safeguarding review started."));
  } catch (e) {
    yield put(createSafeguardingReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while starting safeguarding review!"));
  }
}

// Idempotent per-step save (triage / lead / capacity summary / outcome fields).
export function* patchSafeguardingReviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, formData } = payload;
  try {
    const { data } = yield call(
      api(token).patch,
      `/api/resident/${residentId}/safeguarding-review/${uuid}`,
      formData,
    );
    yield put(patchSafeguardingReviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Saved."));
  } catch (e) {
    yield put(patchSafeguardingReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving safeguarding review!"));
  }
}

// Add a statutory notification row (LA / CQC / police / ICB / other).
export function* addSafeguardingNotificationSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/notification`,
      formData,
    );
    yield put(addSafeguardingNotificationSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Notification recorded."));
  } catch (e) {
    yield put(addSafeguardingNotificationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while recording notification!"));
  }
}

// Instantiate + link a risk-assessment / care-plan / MCA / DoLS record (draft,
// with the lead as responsible employee) in one round trip.
export function* linkSafeguardingRecordSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/link-record`,
      formData,
    );
    yield put(linkSafeguardingRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Record linked."));
  } catch (e) {
    yield put(linkSafeguardingRecordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while linking record!"));
  }
}

// Resolve — outcome + review-due; auto-resolves the source alert. The backend
// enforces the statutory-notification warn+override gate.
export function* resolveSafeguardingReviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/resolve`,
      formData,
    );
    yield put(resolveSafeguardingReviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Safeguarding review resolved."));
  } catch (e) {
    yield put(resolveSafeguardingReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while resolving safeguarding review!"));
  }
}

// The incident records still available to attach to this case (excludes
// anything already linked here or to another review).
export function* listLinkableIncidentsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/linkable-incidents`,
    );
    const incidents = unwrapList(data, "incidents");
    yield put(listLinkableIncidentsSuccess(incidents));
    typeof onSuccess === "function" && onSuccess(incidents);
  } catch (e) {
    yield put(listLinkableIncidentsFailure());
    yield put(
      errorAlert(apiMessage(e, "Error while fetching linkable incidents!")),
    );
  }
}

// Attach one incident record to this review. The api answers with the full
// linked set, so the drawer never has to guess at the new list.
export function* linkSafeguardingIncidentSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, incidentUuid, linkNote } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/link-incident`,
      {
        incident_record_uuid: incidentUuid,
        ...((linkNote || "").trim() ? { link_note: linkNote.trim() } : {}),
      },
    );
    const linked = unwrapList(data, "linked_incidents");
    yield put(linkSafeguardingIncidentSuccess(linked));
    typeof onSuccess === "function" && onSuccess(linked);
    yield put(successAlert("Incident linked to this case."));
  } catch (e) {
    // 422 here is a real guard (different resident / already linked to another
    // case) — pass the api's wording through untouched.
    const message = apiMessage(e, "Error while linking the incident!");
    yield put(linkSafeguardingIncidentFailure());
    typeof onError === "function" && onError(message);
    yield put(errorAlert(message));
  }
}

// Detach an incident record. The originating incident cannot be unlinked (422),
// which is why the row that opened the case hides the action entirely.
export function* unlinkSafeguardingIncidentSaga({
  payload,
  onSuccess,
  onError,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, incidentUuid } = payload || {};
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident/${residentId}/safeguarding-review/${uuid}/link-incident/${incidentUuid}`,
    );
    const linked = unwrapList(data, "linked_incidents");
    yield put(unlinkSafeguardingIncidentSuccess(linked));
    typeof onSuccess === "function" && onSuccess(linked);
    yield put(successAlert("Incident unlinked."));
  } catch (e) {
    const message = apiMessage(e, "Error while unlinking the incident!");
    yield put(unlinkSafeguardingIncidentFailure());
    typeof onError === "function" && onError(message);
    yield put(errorAlert(message));
  }
}

// One event, several residents — the reviews + incidents sharing a reference.
// Read-only: this GATHERS the case, it never merges the reviews.
export function* getSafeguardingCaseSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, reference } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/safeguarding-case/${encodeURIComponent(
        reference,
      )}`,
    );
    const detail = unwrap(data, "case") || null;
    yield put(getSafeguardingCaseSuccess(detail));
    typeof onSuccess === "function" && onSuccess(detail);
  } catch (e) {
    const message = apiMessage(
      e,
      "Error while fetching the safeguarding case!",
    );
    yield put(getSafeguardingCaseFailure());
    // The case view renders the failure inline (it is a read-only panel), so an
    // onError handler suppresses the toast; without one we still alert.
    if (typeof onError === "function") onError(message);
    else yield put(errorAlert(message));
  }
}

export function* listSafeguardingReviewsFlow() {
  yield takeEvery(LIST_SAFEGUARDING_REVIEWS, listSafeguardingReviewsSaga);
}
export function* listSafeguardingCasesFlow() {
  yield takeEvery(LIST_SAFEGUARDING_CASES, listSafeguardingCasesSaga);
}
export function* listSafeguardingHistoryFlow() {
  yield takeEvery(LIST_SAFEGUARDING_HISTORY, listSafeguardingHistorySaga);
}
export function* getSafeguardingReviewFlow() {
  yield takeEvery(GET_SAFEGUARDING_REVIEW, getSafeguardingReviewSaga);
}
export function* createSafeguardingReviewFlow() {
  yield takeEvery(CREATE_SAFEGUARDING_REVIEW, createSafeguardingReviewSaga);
}
export function* patchSafeguardingReviewFlow() {
  yield takeEvery(PATCH_SAFEGUARDING_REVIEW, patchSafeguardingReviewSaga);
}
export function* addSafeguardingNotificationFlow() {
  yield takeEvery(
    ADD_SAFEGUARDING_NOTIFICATION,
    addSafeguardingNotificationSaga,
  );
}
export function* linkSafeguardingRecordFlow() {
  yield takeEvery(LINK_SAFEGUARDING_RECORD, linkSafeguardingRecordSaga);
}
export function* resolveSafeguardingReviewFlow() {
  yield takeEvery(RESOLVE_SAFEGUARDING_REVIEW, resolveSafeguardingReviewSaga);
}
export function* listLinkableIncidentsFlow() {
  yield takeEvery(LIST_LINKABLE_INCIDENTS, listLinkableIncidentsSaga);
}
export function* linkSafeguardingIncidentFlow() {
  yield takeEvery(LINK_SAFEGUARDING_INCIDENT, linkSafeguardingIncidentSaga);
}
export function* unlinkSafeguardingIncidentFlow() {
  yield takeEvery(UNLINK_SAFEGUARDING_INCIDENT, unlinkSafeguardingIncidentSaga);
}
export function* getSafeguardingCaseFlow() {
  yield takeEvery(GET_SAFEGUARDING_CASE, getSafeguardingCaseSaga);
}

export default function* SafeguardingReviewSaga() {
  yield all([
    listSafeguardingReviewsFlow(),
    listSafeguardingCasesFlow(),
    listSafeguardingHistoryFlow(),
    getSafeguardingReviewFlow(),
    createSafeguardingReviewFlow(),
    patchSafeguardingReviewFlow(),
    addSafeguardingNotificationFlow(),
    linkSafeguardingRecordFlow(),
    resolveSafeguardingReviewFlow(),
    listLinkableIncidentsFlow(),
    linkSafeguardingIncidentFlow(),
    unlinkSafeguardingIncidentFlow(),
    getSafeguardingCaseFlow(),
  ]);
}
